import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/apiClient';
import { useMutation } from '../../hooks/useMutation';
import { PRODUCT_NAME } from '../../config/appBranding';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import Input from '../../components/ui/Input';
import '../../components/auth/LoginPage.css';
import './AuthForm.css';

const DEFAULT_WORKSPACE_SLUG = process.env.REACT_APP_DEFAULT_WORKSPACE_SLUG || (process.env.NODE_ENV === 'development' ? 'demo-company' : '');
const GENERIC_MESSAGE = 'If that workspace and email match an active account, password reset instructions will be sent.';

function ForgotPasswordPage() {
  const [workspaceSlug, setWorkspaceSlug] = useState(DEFAULT_WORKSPACE_SLUG);
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');

  const {
    mutate: requestReset,
    isLoading,
    error,
    successMessage
  } = useMutation(
    (payload) => apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
    { successMessage: GENERIC_MESSAGE, errorFallback: GENERIC_MESSAGE }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedWorkspaceSlug = workspaceSlug.trim().toLowerCase();
    const normalizedEmail = email.trim();

    if (!normalizedWorkspaceSlug || !normalizedEmail) {
      setValidationError('Enter your company workspace and email to continue.');
      return;
    }

    setValidationError('');
    await requestReset({
      workspaceSlug: normalizedWorkspaceSlug,
      email: normalizedEmail
    });
  };

  const displayedError = validationError || error;

  return (
    <div className="login-shell">
      <div className="login-panel">
        <div className="login-brand">
          <span className="login-eyebrow">Password Reset</span>
          <h1>{PRODUCT_NAME} access</h1>
          <p>Enter your company workspace and email to request a secure reset link.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <FormField label="Company Workspace" htmlFor="workspace" required>
            <Input
              id="workspace"
              type="text"
              value={workspaceSlug}
              onChange={(event) => setWorkspaceSlug(event.target.value)}
              placeholder="your-workspace"
              autoComplete="organization"
              spellCheck="false"
              error={Boolean(validationError) && !workspaceSlug.trim()}
            />
          </FormField>

          <FormField label="Email" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@northstarops.com"
              autoComplete="email"
              error={Boolean(validationError) && !email.trim()}
            />
          </FormField>

          {displayedError ? (
            <div className="auth-form__alert auth-form__alert--error" role="alert">
              {displayedError}
            </div>
          ) : null}

          {successMessage ? (
            <div className="auth-form__alert auth-form__alert--success" role="status">
              {successMessage}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            loadingLabel="Sending…"
          >
            Send reset link
          </Button>

          <p className="auth-form__help">
            <Link to="/login">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
